import type { EventCatalog, KnownEvent } from '@navio/observability';
import { isAxiosError } from 'noddi-util';
import { useCallback, useMemo, useRef } from 'react';

import { type TrackingFailureProps, commonTrackingEvents } from './events';

const MAX_ERROR_MESSAGE_LENGTH = 120;

type TrackOpenPanel = {
  <E extends KnownEvent>(name: E, props: EventCatalog[E]): void;
  (name: string, props?: Record<string, unknown>): void;
};

type FunnelEvents = {
  started: KnownEvent;
  stepCompleted: KnownEvent;
  created: KnownEvent;
  failed: KnownEvent;
  wizardCompleted?: KnownEvent;
};

type StepOf<E extends KnownEvent> = EventCatalog[E] extends { step: infer S } ? S : never;

type FunnelCore<D extends FunnelEvents> = {
  started: (props?: EventCatalog[D['started']]) => void;
  stepCompleted: (step: StepOf<D['stepCompleted']>) => void;
  created: (props: EventCatalog[D['created']]) => void;
  failed: (props: EventCatalog[D['failed']]) => void;
};

type FunnelFromEvents<D extends FunnelEvents> = D extends { wizardCompleted: infer W extends KnownEvent }
  ? FunnelCore<D> & { wizardCompleted: (props: EventCatalog[W]) => void }
  : FunnelCore<D>;

const OPENPANEL_FUNNELS = {
  fleetSchedule: {
    started: commonTrackingEvents.fleetScheduleStarted,
    stepCompleted: commonTrackingEvents.fleetScheduleStepCompleted,
    created: commonTrackingEvents.fleetScheduleCreated,
    wizardCompleted: commonTrackingEvents.fleetScheduleWizardCompleted,
    failed: commonTrackingEvents.fleetScheduleFailed
  },
  fleetBooking: {
    started: commonTrackingEvents.fleetBookingStarted,
    stepCompleted: commonTrackingEvents.fleetBookingStepCompleted,
    created: commonTrackingEvents.fleetBookingCreated,
    failed: commonTrackingEvents.fleetBookingFailed
  },
  partnerBooking: {
    started: commonTrackingEvents.partnerBookingStarted,
    stepCompleted: commonTrackingEvents.partnerBookingStepCompleted,
    created: commonTrackingEvents.partnerBookingCreated,
    failed: commonTrackingEvents.partnerBookingFailed
  },
  tireOffer: {
    started: commonTrackingEvents.tireOfferStarted,
    stepCompleted: commonTrackingEvents.tireOfferStepCompleted,
    created: commonTrackingEvents.tireOfferCreated,
    failed: commonTrackingEvents.tireOfferFailed
  },
  adminServiceOrg: {
    started: commonTrackingEvents.adminServiceOrgStarted,
    stepCompleted: commonTrackingEvents.adminServiceOrgStepCompleted,
    created: commonTrackingEvents.adminServiceOrgCreated,
    failed: commonTrackingEvents.adminServiceOrgFailed
  }
} as const satisfies Record<string, FunnelEvents>;

const OPENPANEL_ACTIONS = {
  workerCreated: commonTrackingEvents.workerCreated,
  workerContractCreated: commonTrackingEvents.workerContractCreated,
  workerVehicleAssigned: commonTrackingEvents.workerVehicleAssigned
} as const;

function createFunnel<const D extends FunnelEvents>(def: D, trackOpenPanel: TrackOpenPanel): FunnelFromEvents<D> {
  const funnel: FunnelCore<D> = {
    started: (props?: EventCatalog[D['started']]) =>
      trackOpenPanel(def.started, (props ?? {}) as EventCatalog[D['started']]),
    stepCompleted: (step: StepOf<D['stepCompleted']>) =>
      trackOpenPanel(def.stepCompleted, { step } as unknown as EventCatalog[D['stepCompleted']]),
    created: (props: EventCatalog[D['created']]) => trackOpenPanel(def.created, props),
    failed: (props: EventCatalog[D['failed']]) => trackOpenPanel(def.failed, props)
  };

  if (def.wizardCompleted) {
    const wizardCompleted = def.wizardCompleted;
    return {
      ...funnel,
      wizardCompleted: (props: EventCatalog[typeof wizardCompleted]) => trackOpenPanel(wizardCompleted, props)
    } as unknown as FunnelFromEvents<D>;
  }

  return funnel as FunnelFromEvents<D>;
}

function createAction<E extends KnownEvent>(name: E, trackOpenPanel: TrackOpenPanel) {
  return (props: EventCatalog[E]) => trackOpenPanel(name, props);
}

// Factory receives the bound trackOpenPanel. Returns both funnels and actions.
// This file imports only the event names (no tracking singleton) => no cycle.
export function createOpenPanelFunnels(trackOpenPanel: TrackOpenPanel) {
  return {
    funnels: {
      fleetSchedule: createFunnel(OPENPANEL_FUNNELS.fleetSchedule, trackOpenPanel),
      fleetBooking: createFunnel(OPENPANEL_FUNNELS.fleetBooking, trackOpenPanel),
      partnerBooking: createFunnel(OPENPANEL_FUNNELS.partnerBooking, trackOpenPanel),
      tireOffer: createFunnel(OPENPANEL_FUNNELS.tireOffer, trackOpenPanel),
      adminServiceOrg: createFunnel(OPENPANEL_FUNNELS.adminServiceOrg, trackOpenPanel)
    },
    actions: {
      workerCreated: createAction(OPENPANEL_ACTIONS.workerCreated, trackOpenPanel),
      workerContractCreated: createAction(OPENPANEL_ACTIONS.workerContractCreated, trackOpenPanel),
      workerVehicleAssigned: createAction(OPENPANEL_ACTIONS.workerVehicleAssigned, trackOpenPanel)
    }
  };
}

type OpenPanelFunnels = ReturnType<typeof createOpenPanelFunnels>['funnels'];
export type OpenPanelFunnel = OpenPanelFunnels[keyof OpenPanelFunnels];

// Once-guard on started + first-only stepCompleted for this mount. Pass the
// funnel object (`openPanelFunnels.fleetSchedule`), not a name string.
export function useOpenPanelFunnel<T extends OpenPanelFunnel>(funnel: T): T {
  const didStartRef = useRef(false);
  const completedStepsRef = useRef(new Set<string>());

  const started = useCallback(
    (...args: Parameters<T['started']>) => {
      if (didStartRef.current) {
        return;
      }
      didStartRef.current = true;
      completedStepsRef.current.clear();
      (funnel.started as (...a: Parameters<T['started']>) => void)(...args);
    },
    [funnel]
  ) as T['started'];

  const stepCompleted = useCallback(
    (step: Parameters<T['stepCompleted']>[0]) => {
      const key = String(step);
      if (completedStepsRef.current.has(key)) {
        return;
      }
      completedStepsRef.current.add(key);
      (funnel.stepCompleted as (s: Parameters<T['stepCompleted']>[0]) => void)(step);
    },
    [funnel]
  ) as T['stepCompleted'];

  return useMemo(
    (): T => ({
      ...funnel,
      started,
      stepCompleted
    }),
    [started, stepCompleted, funnel]
  );
}

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const PHONE_RE = /\+?\d[\d\s()-]{6,}\d/g;

export function scrubTrackingMessage(message: string): string {
  return message.replace(EMAIL_RE, '[email]').replace(PHONE_RE, '[phone]').slice(0, MAX_ERROR_MESSAGE_LENGTH);
}

/** Build a scrubbed, capped failure payload for funnel `*Failed` events. */
export function toFailureProps<S extends string>(error: unknown, stage: S): TrackingFailureProps & { stage: S };
export function toFailureProps(error: unknown): TrackingFailureProps;
export function toFailureProps(error: unknown, stage?: string): TrackingFailureProps {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  const errorCode = isAxiosError(error)
    ? (error.response?.data as { errors?: Array<{ code?: string }> } | undefined)?.errors?.[0]?.code
    : undefined;
  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : isAxiosError(error) && typeof error.message === 'string'
          ? error.message
          : undefined;

  return {
    stage,
    status,
    errorCode,
    errorMessage: rawMessage ? scrubTrackingMessage(rawMessage) : undefined
  };
}
